// src/hooks/useProjects.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { projectApi } from '../api/projectApi';
import toast from 'react-hot-toast';

const DEFAULT_FILTERS = {
  status: '', priority: '', search: '', page: 1, limit: 10,
};

export const useProjects = (externalFilters = {}) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0, limit: 10 });

  const isMounted = useRef(true);
  const filtersRef = useRef({ ...DEFAULT_FILTERS, ...externalFilters });

  useEffect(() => {
    filtersRef.current = { ...DEFAULT_FILTERS, ...externalFilters };
  });

  const fetchProjects = useCallback(async () => {
    if (!isMounted.current) return;
    setLoading(true);
    setError(null);

    try {
      // Backend: { success, data: [...], total, page, totalPages }
      const res = await projectApi.getAll(filtersRef.current);
      if (!isMounted.current) return;

      const list = Array.isArray(res.data) ? res.data : [];
      const total = res.total ?? res.pagination?.total ?? list.length;
      const pages = res.totalPages ?? res.pagination?.totalPages ?? 1;
      const page = res.page ?? res.pagination?.page ?? 1;

      setProjects(list.filter(Boolean));
      setPagination({ page, total, totalPages: pages, limit: filtersRef.current.limit });
    } catch (err) {
      if (!isMounted.current) return;
      setError(err?.response?.data?.message || 'Errore caricamento progetti');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [JSON.stringify(externalFilters)]); // eslint-disable-line

  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const createProject = async (data) => {
    try {
      const res = await projectApi.create(data);
      toast.success('Progetto creato con successo!');
      fetchProjects();
      return { success: true, data: res.data };
    } catch (err) {
      const message = err?.response?.data?.message || 'Errore creazione progetto';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateProject = async (id, data) => {
    try {
      const res = await projectApi.update(id, data);
      toast.success('Progetto aggiornato!');
      fetchProjects();
      return { success: true, data: res.data };
    } catch (err) {
      const message = err?.response?.data?.message || 'Errore aggiornamento';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const deleteProject = async (id) => {
    try {
      await projectApi.delete(id);
      toast.success('Progetto eliminato!');
      fetchProjects();
      return { success: true };
    } catch (err) {
      const message = err?.response?.data?.message || 'Errore eliminazione';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const getProjectStats = async (id) => {
    try {
      const res = await projectApi.getStats(id);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err?.response?.data?.message || 'Errore statistiche' };
    }
  };

  return {
    projects,
    loading,
    error,
    pagination,
    createProject,
    updateProject,
    deleteProject,
    getProjectStats,
    refreshProjects: fetchProjects,
  };
};